<template>
  <div class="w-full pt-2 flex flex-col gap-4">
    <div class="flex items-center cursor-pointer" @click="emit('back')">
      <ArrowLeft class="h-5 w-5 shrink-0" />
      <span class="flex-grow text-xl font-semibold text-center">Checkout</span>
    </div>

    <div v-if="isLoading" class="flex flex-col items-center justify-center py-8 gap-2">
      <LayupSpinner />
      <p class="text-sm text-grey-light-600">Loading payment form...</p>
    </div>

    <div v-if="errorMessage" class="text-red-500 text-sm p-3 bg-red-50 rounded-lg">
      {{ errorMessage }}
    </div>

    <div v-show="!isLoading && !errorMessage" class="grow">
      <div id="contribute-payment-form" :class="{ 'h-[500px]': checkoutFormLoaded }"></div>
    </div>

    <div class="flex justify-between items-center py-2 border-t">
      <p class="text-sm font-bold">Amount Due</p>
      <p class="text-sm font-bold">{{ displayCurrency(props.amountCents) }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { BASE_URL } from '@/config';
import { NetworkError } from '@/errors';
import { createPayment } from '@/services/payments';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import {
  waitForCheckoutSDK,
  waitForElement,
  type PeachCheckoutEvent,
  type PeachCompletedCheckoutEvent,
  type PeachErrorCheckoutEvent,
} from '@/utils/peach.utils';
import { ArrowLeft } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';

const props = defineProps<{
  paymentPlanId: string;
  amountCents: number;
  paymentMethod: 'CARD' | 'EFT';
}>();

const emit = defineEmits<{
  completed: [paymentId: string];
  cancelled: [];
  back: [];
}>();

const isLoading = ref(true);
const errorMessage = ref('');
const checkoutFormLoaded = ref(false);

const makePayment = async () => {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const notifyUrl = `${BASE_URL}/shopper/payment`;
    const response = await createPayment(
      props.paymentPlanId,
      props.amountCents / 100,
      notifyUrl,
      props.paymentMethod,
      'paymentPlan',
    );

    const { _id: paymentId, checkoutId, entityId } = response;
    await loadCheckoutForm(checkoutId, entityId!, paymentId);
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      errorMessage.value = 'Network error. Please try again.';
    } else {
      createException(error);
      errorMessage.value =
        error?.response?.data || 'An unexpected error occurred. Payment could not be created. Please contact support.';
    }
    isLoading.value = false;
  }
};

const loadCheckoutForm = async (checkoutId: string, entityId: string, paymentId: string) => {
  const sdkLoaded = await waitForCheckoutSDK();
  isLoading.value = false;

  if (!sdkLoaded) {
    createException({
      message: 'Checkout SDK failed to load after multiple attempts.',
      checkoutId,
      userAgent: navigator.userAgent,
    });
    errorMessage.value = 'Our payment provider failed to load. Please check your internet connection and try again.';
    return;
  }

  try {
    const checkout = (window as any).Checkout.initiate({
      checkoutId,
      key: entityId,
      events: {
        onCompleted: (event: PeachCompletedCheckoutEvent) => {
          addMetric('peach_widget', {
            status: 'success',
            value: event.amount,
            result: event.result.code,
            channel: 'shopper_contribute',
          });
          emit('completed', paymentId);
        },
        onCancelled: (event: PeachCheckoutEvent) => {
          addMetric('peach_widget', {
            status: 'cancelled',
            result: event.result.code,
            channel: 'shopper_contribute',
          });
          emit('cancelled');
        },
        onExpired: (event: PeachCheckoutEvent) => {
          addMetric('peach_widget', {
            status: 'expired',
            result: event.result.code,
            channel: 'shopper_contribute',
          });
          emit('cancelled');
        },
        onError: (event: PeachErrorCheckoutEvent) => {
          const error = new Error('Peach widget error.');
          createWarning(error, {
            checkoutId,
            errorCode: event.result.code,
            errorDescription: event.result.description,
            userAgent: navigator.userAgent,
          });
          addMetric('peach_widget', {
            status: 'error',
            result: event.result.code,
            channel: 'shopper_contribute',
          });
          errorMessage.value =
            'Something went wrong while processing the payment. Please try again or contact support.';
        },
      },
      options: {
        theme: {
          brand: {
            primary: '#0f7d88',
          },
        },
      },
    });

    const formElement = await waitForElement('#contribute-payment-form');
    checkout.render(formElement);
    checkoutFormLoaded.value = true;
  } catch (error) {
    createException({
      message: 'Failed to initialize checkout form.',
      checkoutId,
      error,
      userAgent: navigator.userAgent,
    });

    errorMessage.value = 'Unable to load the payment form. Please try again or contact support.';
  }
};

onMounted(() => {
  makePayment();
});
</script>
